// src/App.js
import React, { Suspense, lazy, useMemo, useEffect } from "react";
import { ThemeProvider, CssBaseline, Box } from "@mui/material";
import { createAppTheme } from "./theme/theme";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

import useFeatureFlags from "./hooks/useFeatureFlags";
import PrivateRoute from "./components/PrivateRoute";
import FlagRoute from "./components/FlagRoute";
import AdminRoute from "./components/AdminRoute";
import ScrollToTop from "./components/ScrollToTop";
import { captureFirstTouch } from "./utils/attribution";
import ErrorBoundary from "./components/ErrorBoundary";

// LocalizationProvider is imported locally by each component that needs a date picker
import { useUser } from "./contexts/UserContext";

const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const Landing = lazy(() => import("./pages/Landing"));
const Demo = lazy(() => import("./pages/Demo"));
const ResponsiveAppBar = lazy(() => import("./components/ResponsiveAppBar"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Clients = lazy(() => import("./pages/Clients"));
const Analytics = lazy(() => import("./pages/Analytics"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Appointments = lazy(() => import("./pages/Appointments"));
const BookingHistory = lazy(() => import("./pages/BookingHistory"));
const Notes = lazy(() => import("./pages/Notes"));
const BookingPage = lazy(() => import("./pages/BookingPage"));
const Settings = lazy(() => import("./pages/Settings"));
const Services = lazy(() => import("./pages/Services"));
const Pricing = lazy(() => import("./pages/Pricing"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const StripeInfoPage = lazy(() => import("./pages/StripeInfoPage"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const PaymentsFeaturePage = lazy(
  () => import("./pages/marketing/PaymentsFeaturePage"),
);
const BookingFeaturePage = lazy(
  () => import("./pages/marketing/BookingFeaturePage"),
);
const FeaturePageWrapper = lazy(
  () => import("./components/marketing/FeaturePageWrapper"),
);
const ContactPage = lazy(() => import("./pages/ContactPage"));
const PetProfile = lazy(() => import("./pages/PetProfile"));
const ClientProfile = lazy(() => import("./pages/ClientProfile"));
const AppointmentDetailPage = lazy(() =>
  import("./pages/AppointmentDetailPage"),
);
const GroomingNoteDetail = lazy(() => import("./pages/GroomingNoteDetail"));
const BookingReturnPage = lazy(() => import("./pages/BookingReturnPage"));
const BestGroomingSoftwareBlog = lazy(
  () => import("./pages/marketing/BestGroomingSoftwareBlog"),
);
const DogGroomingSoftwareIreland = lazy(
  () => import("./pages/marketing/DogGroomingSoftwareIreland"),
);
const DogGroomingSoftwareUK = lazy(
  () => import("./pages/marketing/DogGroomingSoftwareUK"),
);
const HowToReduceNoShows = lazy(
  () => import("./pages/marketing/HowToReduceNoShows"),
);
const DogGroomingBusinessGuide = lazy(
  () => import("./pages/marketing/DogGroomingBusinessGuide"),
);
const BlogIndex = lazy(() => import("./pages/marketing/BlogIndex"));
const GroomsyAlternatives = lazy(
  () => import("./pages/marketing/GroomsyAlternatives"),
);
const CompareGroomerIo = lazy(
  () => import("./pages/marketing/compare/CompareGroomerIo"),
);
const CompareGroomsoft = lazy(
  () => import("./pages/marketing/compare/CompareGroomsoft"),
);
const CompareBookd = lazy(
  () => import("./pages/marketing/compare/CompareBookd"),
);
const CompareGroomSlot = lazy(
  () => import("./pages/marketing/compare/CompareGroomSlot"),
);
const FeaturesIndex = lazy(() => import("./pages/marketing/FeaturesIndex"));
const NoShowCalculator = lazy(
  () => import("./pages/marketing/NoShowCalculator"),
);
const Announcements = lazy(() => import("./pages/Announcements"));
const ActionQueue = lazy(() => import("./pages/ActionQueue"));
const Rebooking = lazy(() => import("./pages/Rebooking"));
const Waitlist = lazy(() => import("./pages/Waitlist"));
const Support = lazy(() => import("./pages/Support"));
const ClientRegistrationForm = lazy(
  () => import("./pages/ClientRegistrationForm"),
);
const BookingCancel = lazy(() => import("./pages/BookingCancel"));
const AppointmentSelfService = lazy(() => import("./pages/AppointmentSelfService"));
const InviteBounce = lazy(() => import("./pages/InviteBounce"));
const PublicStatusPage = lazy(() => import("./pages/PublicStatusPage"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const AwaitApproval = lazy(() => import("./pages/AwaitApproval"));
const Financials = lazy(() => import("./pages/Financials"));
const Payments = lazy(() => import("./pages/Payments"));
const Referrals = lazy(() => import("./pages/Referrals"));
const AdminLeads = lazy(() => import("./pages/admin/AdminLeads"));
const AdminNoShowCalculator = lazy(
  () => import("./pages/admin/AdminNoShowCalculator"),
);
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminSignupsTodayDetail = lazy(
  () => import("./pages/admin/AdminSignupsTodayDetail"),
);
const AdminAppointmentsTodayDetail = lazy(
  () => import("./pages/admin/AdminAppointmentsTodayDetail"),
);
const AdminRevenueTodayDetail = lazy(
  () => import("./pages/admin/AdminRevenueTodayDetail"),
);
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminGrowthIntel = lazy(() => import("./pages/admin/AdminGrowthIntel"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminApprovals = lazy(() => import("./pages/admin/AdminApprovals"));
const AdminSystemHealth = lazy(() => import("./pages/admin/AdminSystemHealth"));
const AdminFinancialReports = lazy(
  () => import("./pages/admin/AdminFinancialReports"),
);
const AdminSubscriptions = lazy(
  () => import("./pages/admin/AdminSubscriptions"),
);
const AdminReferrals = lazy(() => import("./pages/admin/AdminReferrals"));
const AdminDelegatedAccess = lazy(() =>
  import("./pages/admin/AdminDelegatedAccess"),
);
const AdminUserDetail = lazy(() => import("./pages/admin/AdminUserDetail"));
const FounderControl = lazy(() => import("./pages/admin/FounderControl"));
const HowToHub = lazy(() => import("./pages/howto/HowToHub"));
const HowToDeposits = lazy(() => import("./pages/howto/HowToDeposits"));
const HowToAvailability = lazy(() => import("./pages/howto/HowToAvailability"));
const HowToReminders = lazy(() => import("./pages/howto/HowToReminders"));
const HowToClientInfo = lazy(() => import("./pages/howto/HowToClientInfo"));
const HowToRegistration = lazy(() => import("./pages/howto/HowToRegistration"));
const HowToServices = lazy(() => import("./pages/howto/HowToServices"));
const HowToBookingHistory = lazy(
  () => import("./pages/howto/HowToBookingHistory"),
);
const HowToReferrals = lazy(() => import("./pages/howto/HowToReferrals"));
const HowToWaitlist = lazy(() => import("./pages/howto/HowToWaitlist"));
const HowToBreedColours = lazy(() => import("./pages/howto/HowToBreedColours"));
const Changelog = lazy(() => import("./pages/Changelog"));
const AdminSupportInbox = lazy(() => import("./pages/admin/AdminSupportInbox"));
const AdminBulkEmail = lazy(() => import("./pages/admin/AdminBulkEmail"));
const AdminBlogPerformance = lazy(() => import("./pages/admin/AdminBlogPerformance"));
const AdminKanban = lazy(() => import("./pages/admin/AdminKanban"));
const AdminEmail = lazy(() => import("./pages/admin/AdminEmail"));
const MainAppBar = lazy(() => import("./components/MainAppBar"));
const WhatsNewModal = lazy(() => import("./components/WhatsNewModal"));
const LiveChatWidget = lazy(() => import("./components/LiveChatWidget"));

// --- Applies brand colour from groomer profile as MUI theme + CSS variable ---
function DynamicThemeProvider({ children }) {
  const { user } = useUser();
  const brandColor = user?.brandColor || "#2951A3";

  const theme = useMemo(() => createAppTheme(brandColor), [brandColor]);

  useEffect(() => {
    document.documentElement.style.setProperty("--brand-primary", brandColor);
    // 10% opacity tint for sidebar active background
    document.documentElement.style.setProperty(
      "--brand-primary-bg",
      brandColor + "1a",
    );
  }, [brandColor]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}

// --- AppRoutes handles all routing logic and onboarding gating ---
function AppRoutes() {
  const location = useLocation();
  const { user } = useUser();
  useFeatureFlags();

  if (user === undefined)
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            border: "3px solid",
            borderColor: "divider",
            borderTopColor: "primary.main",
            animation: "groomsy-spin 0.8s linear infinite",
            "@keyframes groomsy-spin": {
              to: { transform: "rotate(360deg)" },
            },
          }}
        />
      </Box>
    );

  const noAppBarRoutes = ["/onboarding", "/register", "/await-approval"];
  // Self-service pages (/a/:token) and public booking pages (/book/...) are standalone
  // client-facing pages — they have their own internal navbar and must not show the
  // Groomsy marketing nav or the groomer app bar.
  const isStandalonePage =
    noAppBarRoutes.includes(location.pathname) ||
    location.pathname.startsWith("/a/") ||
    location.pathname.startsWith("/book/") ||
    // Public product demo renders its own banner + navigation.
    location.pathname === "/demo" ||
    location.pathname.startsWith("/demo/") ||
    // The founder console brings its own sidebar + top bar (AdminLayout).
    location.pathname.startsWith("/admin");
  // Landing renders its own MobileHeader/StickyNav — showing ResponsiveAppBar too
  // stacks a second sticky header above it, pushing the hero down and leaving a
  // gap of blank space on mobile.
  const hasOwnHeaderRoutes = ["/", "/landing"];
  const shouldShowAppBar =
    !user && !isStandalonePage && !hasOwnHeaderRoutes.includes(location.pathname);
  const shouldShowMainAppBar = !!user && !isStandalonePage;
  const noOffsetRoutes = ["/", "/landing", "/await-approval"];
  // No top offset on standalone pages — they manage their own spacing
  const needsTopOffset =
    !noOffsetRoutes.includes(location.pathname) && !isStandalonePage;

  const ADMIN_EMAIL = "ray@groomsy.io";
  /* const isAdmin = ... kept for onboarding/approval logic */
  const isAdmin = !!user?.email && user.email.toLowerCase() === ADMIN_EMAIL;

  // Helper: only wrap with sidebar if NOT admin
  // const withSidebar = (el) => isAdmin ? el : <ResponsiveSidebar>{el}</ResponsiveSidebar>;
  // REMOVED: PrivateRoute handles sidebar now

  const vettingStatus = (user?.vetting?.status || "pending").toLowerCase();
  const awaitingApproval = !!user && !isAdmin && vettingStatus !== "approved";

  const onOnboarding = location.pathname.startsWith("/onboarding");
  const needsOnboarding = !!user && !user.onboardingComplete && !isAdmin;
  if (needsOnboarding && !onOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  // /demo stays reachable while an account is under review, so new signups can
  // explore the product instead of staring at a waiting screen.
  const approvalSafeRoutes = ["/await-approval", "/support", "/demo"];
  const onAllowedAwaitRoute = approvalSafeRoutes.some((route) =>
    location.pathname.startsWith(route),
  );
  if (awaitingApproval && !needsOnboarding && !onAllowedAwaitRoute) {
    return <Navigate to="/await-approval" replace />;
  }

  return (
    <>
      {/* Live chat widget — shown to logged-in groomers only, not on public client-facing pages */}
      <Suspense fallback={null}>
        <WhatsNewModal
          user={user}
          isAdmin={isAdmin}
          blocked={needsOnboarding || awaitingApproval}
        />
      </Suspense>

      <Suspense fallback={null}>
        {!!user &&
          !isAdmin &&
          !needsOnboarding &&
          !awaitingApproval &&
          !location.pathname.startsWith("/register") &&
          !location.pathname.startsWith("/book") &&
          !location.pathname.startsWith("/demo") && <LiveChatWidget />}
      </Suspense>

      <Suspense fallback={null}>
        {shouldShowAppBar && <ResponsiveAppBar />}
      </Suspense>
      <Suspense fallback={null}>
        {shouldShowMainAppBar && <MainAppBar user={user} />}
      </Suspense>

      {needsTopOffset && (
        <Box
          sx={{
            height: { xs: 54, sm: 64 },
            pt: "env(safe-area-inset-top)",
          }}
        />
      )}

      <Suspense fallback={<Box sx={{ minHeight: "30vh" }} />}>
      {/* Keyed by path so a crash on one page clears on navigation. The demo shares one
          key across /demo/* so its in-memory sample data survives switching tabs. */}
      <ErrorBoundary
        key={
          location.pathname === "/demo" || location.pathname.startsWith("/demo/")
            ? "demo"
            : location.pathname
        }
      >
        <Routes>
          <Route path="/i/:token" element={<InviteBounce />} />
          {/* Client self-service: magic-link cancel / reschedule (no login required) */}
          <Route path="/a/:token" element={<AppointmentSelfService />} />
          <Route path="/status" element={<PublicStatusPage />} />
          <Route path="/register" element={<ClientRegistrationForm />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/how-to" element={<HowToHub />} />
          <Route path="/how-to/deposits" element={<HowToDeposits />} />
          <Route path="/how-to/availability" element={<HowToAvailability />} />
          <Route path="/how-to/reminders" element={<HowToReminders />} />
          <Route path="/how-to/client-info" element={<HowToClientInfo />} />
          <Route path="/how-to/registration" element={<HowToRegistration />} />
          <Route path="/how-to/services" element={<HowToServices />} />
          <Route
            path="/how-to/booking-history"
            element={<HowToBookingHistory />}
          />
          <Route path="/how-to/referrals" element={<HowToReferrals />} />
          <Route path="/how-to/waitlist" element={<HowToWaitlist />} />
          <Route path="/how-to/breed-colours" element={<HowToBreedColours />} />
          <Route path="/features" element={<FeaturesIndex />} />
          <Route path="/features/payments" element={<PaymentsFeaturePage />} />
          <Route path="/features/:slug" element={<FeaturePageWrapper />} />
          <Route path="/blog" element={<BlogIndex />} />
          <Route
            path="/blog/best-dog-grooming-software"
            element={<BestGroomingSoftwareBlog />}
          />
          <Route
            path="/blog/dog-grooming-software-ireland"
            element={<DogGroomingSoftwareIreland />}
          />
          <Route
            path="/blog/dog-grooming-software-uk"
            element={<DogGroomingSoftwareUK />}
          />
          <Route
            path="/blog/how-to-reduce-dog-grooming-no-shows"
            element={<HowToReduceNoShows />}
          />
          <Route
            path="/blog/dog-grooming-business-software-guide"
            element={<DogGroomingBusinessGuide />}
          />
          <Route
            path="/compare/groomsy-vs-alternatives"
            element={<GroomsyAlternatives />}
          />
          <Route
            path="/compare/groomsy-vs-groomer-io"
            element={<CompareGroomerIo />}
          />
          <Route
            path="/compare/groomsy-vs-groomsoft"
            element={<CompareGroomsoft />}
          />
          <Route path="/compare/groomsy-vs-bookd" element={<CompareBookd />} />
          <Route
            path="/compare/groomsy-vs-groomslot"
            element={<CompareGroomSlot />}
          />
          <Route path="/no-show-calculator" element={<NoShowCalculator />} />

          {/* Public live demo — sample data only, no auth required */}
          <Route path="/demo/*" element={<Demo />} />

          <Route
            path="/"
            element={
              user ? (
                isAdmin ? (
                  <Navigate to="/admin/dashboard" replace />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              ) : (
                <Landing />
              )
            }
          />

          {/* /landing redirects to groomsy.io */}
          <Route
            path="/landing"
            element={<Navigate to="https://groomsy.io" replace />}
          />

          <Route path="/contact" element={<ContactPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/learn/stripe" element={<StripeInfoPage />} />
          <Route
            path="/book/:groomerId/success"
            element={<BookingReturnPage />}
          />

          <Route
            path="/login"
            element={
              user ? (
                isAdmin ? (
                  <Navigate to="/admin/dashboard" />
                ) : (
                  <Navigate to="/dashboard" />
                )
              ) : (
                <Login />
              )
            }
          />
          <Route
            path="/signup"
            element={
              user ? (
                isAdmin ? (
                  <Navigate to="/admin/dashboard" />
                ) : (
                  <Navigate to="/dashboard" />
                )
              ) : (
                <Signup />
              )
            }
          />
          <Route
            path="/await-approval"
            element={user ? <AwaitApproval /> : <Navigate to="/login" />}
          />

          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />

          <Route
            path="/grooming-note/:noteId"
            element={
              <PrivateRoute>
                <GroomingNoteDetail />
              </PrivateRoute>
            }
          />

          <Route
            path="/support"
            element={
              <PrivateRoute>
                <Support />
              </PrivateRoute>
            }
          />

          <Route path="/onboarding" element={<Onboarding user={user} />} />

          <Route
            path="/clients"
            element={
              <PrivateRoute>
                <Clients />
              </PrivateRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <PrivateRoute>
                <Analytics />
              </PrivateRoute>
            }
          />

          <Route
            path="/appointments"
            element={
              <PrivateRoute>
                <Appointments />
              </PrivateRoute>
            }
          />

          <Route
            path="/booking-history"
            element={
              <PrivateRoute>
                <BookingHistory />
              </PrivateRoute>
            }
          />

          {/* Public booking routes */}
          <Route path="/book/:groomerId" element={<BookingPage />} />
          <Route path="/book/:groomerId/cancel" element={<BookingCancel />} />

          <Route
            path="/notes"
            element={
              <PrivateRoute>
                <Notes />
              </PrivateRoute>
            }
          />

          <Route
            path="/settings"
            element={
              <PrivateRoute>
                <Settings />
              </PrivateRoute>
            }
          />

          <Route
            path="/services"
            element={
              <PrivateRoute>
                <Services />
              </PrivateRoute>
            }
          />

          <Route
            path="/financials"
            element={
              <PrivateRoute>
                <Financials />
              </PrivateRoute>
            }
          />
          <Route
            path="/payments"
            element={
              <PrivateRoute>
                <Payments />
              </PrivateRoute>
            }
          />
          <Route
            path="/referrals"
            element={
              <PrivateRoute>
                <Referrals />
              </PrivateRoute>
            }
          />
          <Route
            path="/app/payments-info"
            element={
              <PrivateRoute>
                <PaymentsFeaturePage />
              </PrivateRoute>
            }
          />
          <Route
            path="/features/online-booking"
            element={<BookingFeaturePage />}
          />

          <Route
            path="/clients/:clientId/:petIdx"
            element={
              <PrivateRoute>
                <PetProfile />
              </PrivateRoute>
            }
          />

          <Route
            path="/clients/:clientId"
            element={
              <PrivateRoute>
                <ClientProfile />
              </PrivateRoute>
            }
          />

          <Route
            path="/appointments/:apptId"
            element={
              <PrivateRoute>
                <AppointmentDetailPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/action-queue"
            element={
              <PrivateRoute>
                <ActionQueue />
              </PrivateRoute>
            }
          />

          <Route
            path="/announcements"
            element={
              <PrivateRoute>
                <Announcements />
              </PrivateRoute>
            }
          />

          <Route
            path="/rebooking"
            element={
              <FlagRoute flag="enableSmartRebooking">
                <Rebooking />
              </FlagRoute>
            }
          />

          <Route
            path="/waitlist"
            element={
              <FlagRoute flag="enableWaitlist">
                <Waitlist />
              </FlagRoute>
            }
          />

          <Route
            path="/changelog"
            element={
              <PrivateRoute>
                <Changelog />
              </PrivateRoute>
            }
          />

          {/* Legal & marketing */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/pricing" element={<Pricing />} />

          {/* Admin routes (admin-only) */}
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <Navigate to="/admin/dashboard" replace />
              </AdminRoute>
            }
          />

          <Route
            path="/admin/dashboard"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />

          <Route
            path="/admin/leads"
            element={
              <AdminRoute>
                <AdminLeads />
              </AdminRoute>
            }
          />

          <Route
            path="/admin/no-show-calculator"
            element={
              <AdminRoute>
                <AdminNoShowCalculator />
              </AdminRoute>
            }
          />

          <Route
            path="/admin/approvals"
            element={
              <AdminRoute>
                <AdminApprovals />
              </AdminRoute>
            }
          />

          <Route
            path="/admin/founder"
            element={
              <AdminRoute>
                <FounderControl />
              </AdminRoute>
            }
          />

          <Route
            path="/admin/users"
            element={<Navigate to="/admin/dashboard" replace />}
          />

          <Route
            path="/admin/users/:uid"
            element={
              <AdminRoute>
                <AdminUserDetail />
              </AdminRoute>
            }
          />

          <Route
            path="/admin/growth-intel"
            element={
              <AdminRoute>
                <AdminGrowthIntel />
              </AdminRoute>
            }
          />

          <Route
            path="/admin/analytics"
            element={
              <AdminRoute>
                <AdminAnalytics />
              </AdminRoute>
            }
          />

          <Route
            path="/admin/settings"
            element={
              <AdminRoute>
                <AdminSettings />
              </AdminRoute>
            }
          />

          <Route
            path="/admin/system-health"
            element={
              <AdminRoute>
                <AdminSystemHealth />
              </AdminRoute>
            }
          />

          <Route
            path="/admin/subscriptions"
            element={
              <AdminRoute>
                <AdminSubscriptions />
              </AdminRoute>
            }
          />

          <Route
            path="/admin/financial-reports"
            element={
              <AdminRoute>
                <AdminFinancialReports />
              </AdminRoute>
            }
          />

          <Route
            path="/admin/referrals"
            element={
              <AdminRoute>
                <AdminReferrals />
              </AdminRoute>
            }
          />

          <Route
            path="/admin/delegated-access"
            element={
              <AdminRoute>
                <AdminDelegatedAccess />
              </AdminRoute>
            }
          />

          <Route
            path="/admin/support"
            element={
              <AdminRoute>
                <AdminSupportInbox />
              </AdminRoute>
            }
          />

          <Route
            path="/admin/bulk-email"
            element={
              <AdminRoute>
                <AdminBulkEmail />
              </AdminRoute>
            }
          />

          <Route
            path="/admin/blog"
            element={
              <AdminRoute>
                <AdminBlogPerformance />
              </AdminRoute>
            }
          />

          <Route
            path="/admin/email"
            element={
              <AdminRoute>
                <AdminEmail />
              </AdminRoute>
            }
          />

          <Route
            path="/admin/kanban"
            element={
              <AdminRoute>
                <AdminKanban />
              </AdminRoute>
            }
          />

          <Route
            path="/admin/user-insights"
            element={<Navigate to="/admin/dashboard" replace />}
          />

          <Route
            path="/admin/today/signups"
            element={
              <AdminRoute>
                <AdminSignupsTodayDetail />
              </AdminRoute>
            }
          />

          <Route
            path="/admin/today/appointments"
            element={
              <AdminRoute>
                <AdminAppointmentsTodayDetail />
              </AdminRoute>
            }
          />

          <Route
            path="/admin/today/revenue"
            element={
              <AdminRoute>
                <AdminRevenueTodayDetail />
              </AdminRoute>
            }
          />

          {/* Fallback */}
          <Route
            path="*"
            element={<Navigate to={user ? "/dashboard" : "/"} />}
          />
        </Routes>
      </ErrorBoundary>
      </Suspense>
    </>
  );
}

// Before any route redirects, so the landing page recorded is the real one.
captureFirstTouch();

function App() {
  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <DynamicThemeProvider>
        <ScrollToTop />
        <AppRoutes />
      </DynamicThemeProvider>
    </Router>
  );
}

export default App;
